// OL Finance — Linked banks (live feed). The Treasurer links the org's bank in
// Stripe's own pop-up (Stripe Financial Connections on Semprion's Stripe — OL never
// sees bank logins), picks which OL Finance account each bank account feeds, and
// Sync pulls the balance + new posted transactions into For review.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Button } from '../../components/ui/button';
import { money, fmtDate } from '../money';
import { Combo } from './FormFrame';
import { Landmark, RefreshCw, Loader2, Trash2 } from 'lucide-react';

const errOf = (e) => e?.response?.data?.detail || e?.message || 'Request failed';

export default function BankLinks({ base, onSynced }) {
  const [d, setD] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(() => axios.get(`${base}/bank-links`).then((r) => setD(r.data)).catch(() => setD(null)), [base]);
  useEffect(() => {
    load();
    axios.get(`${base}/accounts`).then((r) => setAccounts((r.data?.accounts || []).filter((a) => ['Bank', 'Credit Card'].includes(a.type) && a.is_active))).catch(() => {});
  }, [base, load]);
  if (!d || (!d.enabled && !(d.links || []).length)) return null;

  const link = async () => {
    setBusy('link'); setMsg('');
    try {
      const { data } = await axios.post(`${base}/bank-links/session`, { return_url: window.location.href });
      const stripe = await loadStripe(data.publishable_key);
      const res = await stripe.collectFinancialConnectionsAccounts({ clientSecret: data.client_secret });
      if (res.error) throw new Error(res.error.message);
      const n = res.financialConnectionsSession?.accounts?.length || 0;
      if (!n) { setMsg('No account was linked.'); return; }
      const r = await axios.post(`${base}/bank-links/complete`, { session_id: data.session_id });
      setD(r.data); setMsg(`${n} account${n === 1 ? '' : 's'} linked — choose which OL Finance account each one feeds, then Sync.`);
    } catch (e) { setMsg(errOf(e)); } finally { setBusy(''); }
  };
  const mapTo = async (l, account_id) => {
    try { await axios.put(`${base}/bank-links/${l.link_id}`, { account_id: account_id || null }); load(); }
    catch (e) { setMsg(errOf(e)); }
  };
  const sync = async (l) => {
    setBusy(l.link_id); setMsg('');
    try {
      const { data } = await axios.post(`${base}/bank-links/${l.link_id}/sync`);
      setMsg(`${l.institution || 'Bank'} ••${l.last4}: ${data.imported} new transaction${data.imported === 1 ? '' : 's'} to review.`);
      load(); onSynced?.();
    } catch (e) { setMsg(errOf(e)); } finally { setBusy(''); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Stop the live feed from ${l.institution || 'this bank'} ••${l.last4}? Transactions already imported stay.`)) return;
    try { await axios.delete(`${base}/bank-links/${l.link_id}`); load(); } catch (e) { setMsg(errOf(e)); }
  };
  const opts = [{ id: '', label: '— choose account —' }, ...accounts.map((a) => ({ id: a.account_id, label: `${a.number ? `${a.number} ` : ''}${a.full_name || a.name}` }))];

  return (
    <div className="mb-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40 p-3" data-testid="bank-links">
      <div className="flex items-center gap-2">
        <Landmark className="h-4 w-4 text-emerald-500" />
        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">Linked banks</span>
        {d.mode === 'test' && <span className="text-[10px] uppercase rounded border border-amber-500/50 text-amber-500 px-1.5">test mode</span>}
        <span className="text-xs text-slate-500">Balances and new transactions come straight from your bank, daily or when you Sync.</span>
        <div className="flex-1" />
        {d.enabled && (
          <Button onClick={link} disabled={!!busy} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="bank-link-add">
            {busy === 'link' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Link a bank'}
          </Button>
        )}
      </div>
      {(d.links || []).length > 0 && (
        <div className="mt-2 space-y-1.5">
          {d.links.map((l) => (
            <div key={l.link_id} className="flex flex-wrap items-center gap-3 text-xs" data-testid={`bank-link-${l.link_id}`}>
              <span className="w-56 truncate text-slate-800 dark:text-slate-200">{l.institution || 'Bank'} · {l.display_name || l.category} ••{l.last4}</span>
              <span className="text-slate-500">feeds</span>
              <div className="w-64"><Combo value={l.account_id || ''} onChange={(v) => mapTo(l, v)} options={opts} testid={`bank-link-acct-${l.link_id}`} /></div>
              <span className="text-slate-500">{l.balance != null ? `Bank balance ${money(l.balance)} (${fmtDate(l.balance_as_of)})` : ''}</span>
              <span className="text-slate-400">{l.last_sync_at ? `synced ${new Date(l.last_sync_at).toLocaleString()}` : 'never synced'}</span>
              {l.status && l.status !== 'active' && <span className="text-amber-500">{l.status} — link the bank again</span>}
              <div className="flex-1" />
              <button onClick={() => sync(l)} disabled={!!busy || !l.account_id} title={l.account_id ? 'Sync now' : 'Choose an account first'} className="text-emerald-600 disabled:text-slate-400" data-testid={`bank-link-sync-${l.link_id}`}>
                {busy === l.link_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </button>
              <button onClick={() => remove(l)} title="Stop the live feed" className="text-slate-400 hover:text-red-500" data-testid={`bank-link-remove-${l.link_id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      {msg && <div className="mt-2 text-xs text-slate-600 dark:text-slate-300" data-testid="bank-link-msg">{msg}</div>}
    </div>
  );
}
